<template>
    <app-layout>
        <template #header>
            <FarmTitle :farm="farm">
                Pferde
                <template v-slot:buttons>
                    <div class="mt-4 flex-shrink-0 flex md:mt-0 md:ml-4">
                        <inertia-link :href="route('horses.stables.simplify_prices', {farm: farm})" class="mr-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Preise vereinheitlichen
                        </inertia-link>
                        <inertia-link :href="route('product.prices.massAdd_if', {farm: farm})" class="mr-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Allen Pferden hinzufügen
                        </inertia-link>
                        <inertia-link :href="route('export.copy', {export: farm.last_export_id})" class="mr-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Aus letztem Export übernehmen
                        </inertia-link>
                        <inertia-link :href="route('farm.products.clear', {farm: farm})" :onBefore="() => confirm('Wirklich alle Produkte/Zuordnungen, welche noch nicht exportiert wurden, löschen?')" class="mr-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Alle hinzugefügten Produkte löschen
                        </inertia-link>
                        <inertia-link :href="route('horse.onboard', {farm: farm})" class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Onboarding
                        </inertia-link>
                    </div>
                </template>
            </FarmTitle>
        </template>

        <div class="py-12">
            <div class="max-w-full mx-auto sm:px-6 lg:px-8">
                <div class="mb-4">
                    <label for="search" class="block text-sm font-medium text-gray-700">Suche</label>
                    <div class="mt-1">
                        <input type="text" name="search" id="search" v-model="search" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Pferdinand">
                    </div>
                </div>
                <div class="mb-6">
                    <dl class="mt-5 grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow md:grid-cols-2 md:divide-x md:divide-y-0">
                    <div v-for="item in stats" :key="item.name" class="px-4 py-5 sm:p-6">
                        <dt class="text-base font-normal text-gray-900">{{ item.name }}</dt>
                        <dd class="mt-1 flex items-baseline justify-between md:block lg:flex">
                        <div class="flex items-baseline text-2xl font-semibold text-indigo-600">
                            {{ item.stat }}
                            <span class="ml-2 text-sm font-medium text-gray-500">von {{ item.previousStat }}</span>
                        </div>

                        <div :class="[item.changeType === 'increase' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800', 'inline-flex items-baseline rounded-full px-2.5 py-0.5 text-sm font-medium md:mt-2 lg:mt-0']">
                            <ArrowUpIcon v-if="item.changeType === 'increase'" class="-ml-1 mr-0.5 h-5 w-5 flex-shrink-0 self-center text-green-500" aria-hidden="true" />
                            <ArrowDownIcon v-else class="-ml-1 mr-0.5 h-5 w-5 flex-shrink-0 self-center text-red-500" aria-hidden="true" />
                            <span class="sr-only"> {{ item.changeType === 'increase' ? 'Erhöht' : 'Decreased' }} um </span>
                            {{ item.change }}
                        </div>
                        </dd>
                    </div>
                    </dl>
                </div>
                <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
                    <div class="flex flex-col">
                        <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                            <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <thead class="bg-gray-50">
                                        <tr>
                                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" @click="sort('name')">
                                                Name
                                            </th>
                                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" @click="sort('customer_id')">
                                                Besitzer
                                            </th>
                                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" @click="sort('current_stable_name')">
                                                Stall
                                            </th>
                                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            </th>
                                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" @click="sort('probable_invoice_sum')">
                                                Vor. Preis
                                            </th>
                                            <th scope="col" class="relative px-6 py-3">
                                                <span class="sr-only">Bearbeiten</span>
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <!-- Odd row -->
                                        <template v-if="horses" v-for="(horse, i) in sortedHorses.filter(x => {
                                            if(search == '') return true;
                                            for(y of [x.name, x.besitzer, x.kdnr, x.current_stable?.name]) {if(y && y.toLowerCase().includes(search.toLowerCase())) return true}
                                            return false;
                                            //return x.name.toLowerCase().includes(search.toLowerCase()) || x.besitzer.toLowerCase().includes(search.toLowerCase()) || x.kdnr.toLowerCase().includes(search.toLowerCase()) || x.current_stable?.name.toLowerCase().includes(search.toLowerCase())
                                        })">
                                            <tr :class="i%2 == 0 ? 'bg-white' : 'bg-gray-50'">
                                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {{ horse.name }}
                                                </td>
                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 inline-flex">
                                                    <VueNextSelect
                                                        :options="customers"
                                                        :model-value="horse.customer_id"
                                                        :label-by="(x) => x.name+' ('+x.kdnr+')'"
                                                        value-by="id"
                                                        searchable
                                                        clear-on-close
                                                        close-on-select
                                                        search-placeholder="Besitzer auswählen"
                                                        @update:modelValue="(x) => updateOwner(x, horse)"
                                                    />
                                                    <button @click="add_customer = true" type="button" class="ml-4 inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                        <PlusIcon class="h-5 w-5" aria-hidden="true" />
                                                    </button>
                                                </td>
                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <span v-if="horse.current_stable">
                                                        {{ horse.current_stable?.name }}
                                                        ({{ horse.current_stable?.pivot?.price ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(horse.current_stable?.pivot?.price) : horse.probable_invoice?.find(x => x.type === 'stable_price')?.value ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(horse.probable_invoice?.find(x => x.type === 'stable_price')?.value) : "Lädt" }})
                                                    </span>
                                                    <button @click="updateStable(horse)" class="text-primary-600 hover:text-primary-900"><PencilIcon class="h-5 w-5 inline" aria-hidden="true" /></button>
                                                </td>
                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-wrap max-w-[300px]">
                                                    <template v-for="(item, i) in horse.probable_invoice?.filter(x => x.type !== 'stable_price')">
                                                        <template v-if="item.type !== 'stable_price'">
                                                            {{ i > 0 ? ', ' : ''}}
                                                            {{ item.quantity }}x {{ item.name }}
                                                        </template>
                                                    </template>
                                                    <button @click="getPrice(horse)" class="text-primary-600 hover:text-primary-900"><PencilIcon class="h-5 w-5 inline" aria-hidden="true" /></button>
                                                    <span v-if="horse.next_stable && !horse.current_stable" class="float-left inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 v-full">
                                                        Zieht ein in {{ horse.next_stable.name }} am {{ new Intl.DateTimeFormat().format(new Date(horse.next_stable.pivot.entered_at)) }}
                                                    </span>
                                                    <span v-else-if="horse.next_stable" class="float-left inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                                                        Zieht um (intern) nach {{ horse.next_stable.name }} am {{ new Intl.DateTimeFormat().format(new Date(horse.next_stable.pivot.entered_at)) }}
                                                    </span>
                                                    <span v-else-if="!horse.current_stable" class="float-left inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                                        keinem Stall zugeordnet
                                                    </span>
                                                    <span v-else-if="horse.current_stable?.pivot?.leaving_at" class="float-left inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                                        Zieht aus am {{ new Intl.DateTimeFormat().format(new Date(horse.current_stable.pivot.leaving_at)) }}
                                                    </span>
                                                </td>
                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <span v-if="final_loaded">{{ new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(horse.probable_invoice_sum) }} </span>
                                                    <span v-else>Lädt </span>
                                                </td>
                                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <inertia-link :href="route('horse.offboard', horse)" class="text-primary-600 hover:text-primary-900" v-if="horse.current_stable">Auszug</inertia-link>
                                                    <span @click="$inertia.post(route('horse.archive', horse))" class="text-primary-600 hover:text-primary-900" v-if="!horse.current_stable">Archivieren</span>
                                                </td>
                                            </tr>
                                        </template>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <customer-modal
          :add_customer="add_customer"
          :farm="farm"
        ></customer-modal>
        <jet-dialog-modal :show="showprices" :max-width="'5xl'">
            <jet-action-message :on="book_item.recentlySuccessful" class="mb-3">
                {{ $page.props.flash.success }}
            </jet-action-message>
            <template #title>
                <span class="isolate inline-flex rounded-md shadow-sm mr-1">
                    <button @click="goBack" :disabled="currentIndex === 0" type="button" class="relative inline-flex items-center rounded-l-md bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
                      <span class="sr-only">Previous</span>
                      <ChevronLeftIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                    <button @click="goForward" :disabled="currentIndex === sortedHorses.length - 1" type="button" class="relative -ml-px inline-flex items-center rounded-r-md bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
                      <span class="sr-only">Next</span>
                      <ChevronRightIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                </span>
                Preiszusammensetzung {{ showprices.name }} ({{ new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(showprices.probable_invoice_sum) }})
            </template>

            <template #content>
                <div class="flex flex-col">
                    <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                            <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                <table class="min-w-full divide-y divide-gray-200">
                                    <thead class="bg-gray-50">
                                    <tr>
                                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Datum
                                        </th>
                                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Summe
                                        </th>
                                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Ust
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(item, itemIdx) in showprices.probable_invoice" :class="itemIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {{ item.name }} ({{ new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(item.full_value) }})
                                            <span class="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium ml-4" :class="item.type == 'stable_price' ? 'bg-indigo-100 text-indigo-800' : 'bg-yellow-100 text-yellow-800'">
                                                {{ item.type == 'stable_price' ? 'Stallmiete' : 'Zubuchoptionen' }}
                                            </span>
                                            <span v-if="item.type == 'extra'" class="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium ml-4 bg-green-100 text-green-800">
                                                {{ item.quantity }}x
                                            </span>
                                            <br>
                                            <span class="text-gray-400 text-xs">{{ item.desc }}</span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <template v-if="item.type == 'stable_price'">
                                                {{ new Intl.DateTimeFormat('de-DE').format(new Date(item.date_from)) }}
                                                -
                                                {{ new Intl.DateTimeFormat('de-DE').format(new Date(item.date_till)) }}
                                                ({{ new Intl.NumberFormat('de-DE', { maximumFractionDigits: 4 }).format(item.months) }} Monat{{ item.months == 1 ? '' : 'e' }})
                                            </template>
                                            <template v-else>
                                                {{ new Intl.DateTimeFormat('de-DE').format(new Date(item.date)) }}
                                            </template>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {{ new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 4 }).format(item.value) }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {{ item.vat }}
                                            <template v-if="item.type == 'extra'">
                                                <button @click="$inertia.delete(route('price.delete', {price: item.id}), {
                                                    onSuccess: () => {
                                                        getPrice(horses.find(x => x.id === showprices.id))
                                                    }
                                                }) " class="text-primary-600 hover:text-primary-900">
                                                    Löschen
                                                </button>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr :class="showprices.probable_invoice?.size % 2 === 0 ? 'bg-gray-50' : 'bg-white'">
                                        <td></td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">Summe</td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {{ new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(showprices.probable_invoice_sum) }}
                                        </td>
                                        <td></td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
                <h1 class="mt-5 text-base font-semibold text-indigo-600 tracking-wide uppercase">Produkt buchen</h1>
                <div class="md:grid-cols-12 md:grid md:gap-4 mt-3">
                    <div class="md:col-span-8">
                        <label for="Datum" class="block text-sm font-medium text-gray-700" >
                            Datum
                        </label>
                        <div class="mt-1">
                            <input type="date"  id="Datum" v-model="book_item.date" autocomplete="date" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" />
                            <jet-input-error :message="book_item.errors.date" />
                        </div>
                    </div>
                    <div class="md:col-span-4">
                        <label for="vat" class="block text-sm font-medium text-gray-700" >
                            Menge
                        </label>
                        <div class="mt-1">
                            <input type="number" id="vat" v-model="book_item.quantity" autocomplete="date" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" />
                            <jet-input-error :message="book_item.errors.quantity" />
                        </div>
                    </div>
                    <div class="md:col-span-6">
                        <label for="product" class="block text-sm font-medium text-gray-700" >
                            Produkt
                        </label>
                        <div class="mt-1">
                            <VueNextSelect
                                id="product"
                                :options="products"
                                :label-by="(x) => x.name + ' ('+ x.item_number + ')'"
                                value-by="id"
                                searchable
                                clear-on-close
                                close-on-select
                                search-placeholder="Produkt auswählen"
                                v-model="book_item.product"
                            />
                            <jet-input-error :message="book_item.errors.product" />
                        </div>
                    </div>
                    <div class="md:col-span-6">
                        <label for="product" class="block text-sm font-medium text-gray-700" >
                            Preis pro Einheit
                        </label>
                        <div class="mt-1">
                            <template v-if="book_item.product">
                                {{ new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(products.find(x => x.id == book_item.product)?.all_prices?.find(x => new Date(x.in_effect_from) < new Date(book_item.date) && (new Date(x.in_effect_till) > new Date(book_item.date) || !x.in_effect_till))?.value) }}
                                ({{ products.find(x => x.id == book_item.product)?.all_prices?.find(x => new Date(x.in_effect_from) < new Date(book_item.date) && (new Date(x.in_effect_till) > new Date(book_item.date) || !x.in_effect_till))?.vat }} % Ust.)
                            </template>
                            <template v-else>
                                Bitte zunächst Datum und Produkt auswählen
                            </template>
                        </div>
                    </div>
                    <div class="md:col-span-12">
                        <label for="desc" class="block text-sm font-medium text-gray-700" >
                            Beschreibung
                        </label>
                        <div class="mt-1">
                            <textarea id="desc" v-model="book_item.desc" autocomplete="date" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" />
                            <jet-input-error :message="book_item.errors.desc" />
                        </div>
                    </div>
                    <div class="flex justify-end md:col-span-12">
                        <button @click="book_item.post(route('horse.product.store',{horse: showprices}),{preserveScroll: true, onSuccess: ()=>{getPrice(horses.find(x=> x.id===showprices.id))}})" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Preis hinzufügen
                        </button>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex justify-between">
                  <span class="isolate inline-flex rounded-md shadow-sm">
                    <button @click="goBack" :disabled="currentIndex === 0" type="button" class="relative inline-flex items-center rounded-l-md bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
                      <span class="sr-only">Previous</span>
                      <ChevronLeftIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                    <button @click="goForward" :disabled="currentIndex === sortedHorses.length - 1" type="button" class="relative -ml-px inline-flex items-center rounded-r-md bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
                      <span class="sr-only">Next</span>
                      <ChevronRightIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                  </span>
                  <button @click="showprices = false" type="button" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    Schließen
                  </button>
                </div>
            </template>
        </jet-dialog-modal>
        <jet-dialog-modal :show="stable_change">
            <template #title>
                Stall von {{ stable_change_form.horse?.name }} bearbeiten
            </template>

            <template #content>
                <div class="mt-6 md:grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div class="col-span-6" v-if="stable_change_form.horse?.current_stable">
                        <div class="flex items-center justify-between">
                          <span class="flex-grow flex flex-col" id="is-exiting">
                            <span class="text-sm font-medium text-gray-900">Auszug</span>
                            <span class="text-sm text-gray-500">Das Pferd wird nicht in einen neuen Stall ziehen</span>
                          </span>
                            <button type="button" @click="stable_change_form.exiting = !stable_change_form.exiting" :class="stable_change_form.exiting ? 'bg-indigo-600' : 'bg-gray-200'" class="bg-gray-200 relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" role="switch" aria-checked="false" aria-labelledby="availability-label">
                                <span class="sr-only">Auszug</span>
                                <span aria-hidden="true" :class="stable_change_form.exiting ? 'translate-x-5' : 'translate-x-0'" class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                            </button>
                        </div>
                    </div>
                    <div class="col-span-6">
                        <label for="date" class="block text-sm font-medium text-gray-700" >
                            <template v-if="!stable_change_form.horse?.current_stable">Einzugs</template><template v-else-if="!stable_change_form.exiting">Umzugs</template><template v-else>Auszugs</template>datum
                        </label>
                        <div class="mt-1">
                            <input type="date" name="date" id="date" v-model="stable_change_form.date" autocomplete="date" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" />
                            <jet-input-error :message="stable_change_form.errors.date" />
                        </div>
                    </div>
                    <template v-if="!stable_change_form.exiting">
                        <div class="col-span-6">
                            <label for="stable" class="block text-sm font-medium text-gray-700">
                                Neuer Stall
                            </label>
                            <div class="mt-1">
                                <VueNextSelect
                                    id="stable"
                                    :options="stables"
                                    label-by="name"
                                    value-by="id"
                                    searchable
                                    clear-on-close
                                    close-on-select
                                    search-placeholder="Stall auswählen"
                                    v-model="stable_change_form.stable"
                                    @update:modelValue="updateStablePrice()"
                                />
                                <jet-input-error :message="stable_change_form.errors.stable" />
                            </div>
                        </div>
                        <template v-if="stable_change_form.stable">
                            <div class="col-span-3">
                                <label for="stable_product" class="block text-sm font-medium text-gray-700" >
                                    Produkt
                                </label>
                                <div class="mt-1">
                                    <VueNextSelect
                                        id="stable_product"
                                        :options="stable_products"
                                        label-by="name"
                                        value-by="id"
                                        searchable
                                        clear-on-close
                                        close-on-select
                                        search-placeholder="Stall auswählen"
                                        v-model="stable_change_form.product"
                                    />
                                    <jet-input-error :message="stable_change_form.errors.product" />
                                </div>
                            </div>
                            <div class="col-span-6">
                                <label for="price_overrule" class="block text-sm font-medium text-gray-700" >
                                    Preis überschreiben?
                                </label>
                                <div class="mt-1">
                                    <input type="checkbox" name="price_overrule" id="price_overrule" v-model="stable_change_form.price_overrule" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" />
                                    <jet-input-error :message="stable_change_form.errors.price_overrule" />
                                </div>
                            </div>
                            <template v-if="stable_change_form.price_overrule">
                            <div class="col-span-6">
                                <label for="price" class="block text-sm font-medium text-gray-700" >
                                    Preis
                                </label>
                                <div class="mt-1">
                                    <input type="number" name="price" id="price" v-model="stable_change_form.price" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" />
                                    <jet-input-error :message="stable_change_form.errors.price" />
                                </div>
                            </div>
                            <div class="col-span-6">
                                <label for="form_vat" class="block text-sm font-medium text-gray-700" >
                                    Ust
                                </label>
                                <div class="mt-1">
                                    <input type="number" name="vat" id="form_vat" v-model="stable_change_form.vat" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" />
                                    <jet-input-error :message="stable_change_form.errors.vat" />
                                </div>
                            </div>
                            </template>
                        </template>
                    </template>

                    <!--div class="col-span-6">
                        <label for="discount" class="block text-sm font-medium text-gray-700" >
                            Rabatt (von Mtl. Preis i.H.v. {{ !stable_change.empty ? stable_change.stable.current_price.pivot.value : stable_change.stable.current_price.pivot.value_empty }} € abziehen)
                        </label>
                        <div class="mt-1">
                            <input type="number" name="discount" id="discount" v-model="stable_change.discount" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" />
                            <jet-input-error :message="stable_change.errors.discount" />
                        </div>
                    </div>
                    <div class="col-span-6">
                        <div class="flex items-center justify-between">
                          <span class="flex-grow flex flex-col" id="empty_box">
                            <span class="text-sm font-medium text-gray-900">Als Leerbox</span>
                            <span class="text-sm text-gray-500">Den Stall als Leerbox buchen. (Mtl.: {{  stable_change.stable.current_price.pivot.value_empty ?? 0 }} €)</span>
                          </span>
                            <button type="button" @click="stable_change.empty = !stable_change.empty" :class="stable_change.empty ? 'bg-indigo-600' : 'bg-gray-200'" class="bg-gray-200 relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" role="switch" aria-checked="false" aria-labelledby="availability-label">
                                <span class="sr-only">Use setting</span>
                                <span aria-hidden="true" :class="stable_change.empty ? 'translate-x-5' : 'translate-x-0'" class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                            </button>
                        </div>
                    </div-->
                    <div class="col-span-6" v-if="new Date(farm.last_export).getTime() > new Date(stable_change_form.date)">
                        <div class="flex items-center justify-between">
                          <span class="flex-grow flex flex-col" id="availability-label">
                            <span class="text-sm font-medium text-gray-900">Differenzbetrag seit Abrechnung berechnen</span>
                            <span class="text-sm text-gray-500">Differenzbetrag seit der letzten Abrechnung ({{ new Intl.DateTimeFormat().format(new Date(farm.last_export)) }}) berechnen und anrechnen. Sollte dies nicht ausgewählt werden, so wird der erste Tag nach der letzten Abrechnung als Umzugstag genommen und die Differenz nicht erstattet/abgebucht.</span>
                          </span>
                            <button type="button" @click="stable_change_form.refund_difference = !stable_change_form.refund_difference" :class="stable_change_form.refund_difference ? 'bg-indigo-600' : 'bg-gray-200'" class="bg-gray-200 relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" role="switch" aria-checked="false" aria-labelledby="availability-label">
                                <span class="sr-only">Differenzbetrag berechnen</span>
                                <span aria-hidden="true" :class="stable_change_form.refund_difference ? 'translate-x-5' : 'translate-x-0'" class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                            </button>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex justify-end">
                    <button @click="stable_change = false" :disabled="stable_change_form.processing" type="button" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        Abbrechen
                    </button>
                    <button :disabled="stable_change_form.processing" @click="stable_change_form.post(route('horse.update.stable', {horse: stable_change_form.horse}), {
                        onSuccess: () => {
                            stable_change_form.reset();
                            stable_change = false;
                        }
                    })" class="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        Stall ändern
                    </button>
                </div>
            </template>
        </jet-dialog-modal>
    </app-layout>
</template>

<script>
    import axios from 'axios';
    import AppLayout from "@/Layouts/AppLayout";
    import HorseEdit from "@/Modules/HorseEdit";
    import Button from "@/Jetstream/Button";
    import FarmTitle from "@/Layouts/FarmTitle";
    import VueNextSelect from 'vue-next-select';
    import { PencilIcon } from "@heroicons/vue/outline";
    import { PlusIcon, ChevronLeftIcon, ChevronRightIcon, ArrowUpIcon, ArrowDownIcon } from "@heroicons/vue/solid";
    import JetDialogModal from "@/Jetstream/DialogModal";
    import OnBoarding from "@/Pages/Customers/OnBoarding";
    import JetInputError from "@/Jetstream/InputError";
    import JetActionMessage from "@/Jetstream/ActionMessage";
    import CustomerModal from "@/Pages/Modals/CustomerModal.vue";

    export default {
        name: "Farm",
        components: {
            CustomerModal,
            FarmTitle, Button, AppLayout, VueNextSelect, PlusIcon, JetDialogModal, OnBoarding, JetInputError, ChevronLeftIcon, ChevronRightIcon, PencilIcon, ArrowUpIcon, ArrowDownIcon},
        props: ['farm', 'loaded_horses', 'customers', 'stables', 'customer', 'products', 'stable_products', 'last_export'],
        data: function () {
            return {
                search: '',
                add_customer: false,
                stable_date: false,
                stable_change: false,
                stable_change_form: this.$inertia.form({
                    horse: null,
                    date: new Date().toISOString().split('T')[0],
                    refund_difference: false,
                    exiting: false,
                    stable: null,
                    product: null,
                    price: 0,
                    vat: 19,
                    price_overrule: false
                }),
                book_item: this.$inertia.form({
                    date: new Date().toISOString().split('T')[0],
                    product: null,
                    quantity: 1,
                    desc: ''
                }),
                changing_horse: null,
                showprices: false,
                final_loaded: false,
                horses: this.loaded_horses ?? [],
                currentSort:'name',
                currentSortDir:'asc'
            }
        },
        computed:{
            sortedHorses() {
                return this.horses.toSorted((a, b) => {
                    let aValue = a[this.currentSort];
                    let bValue = b[this.currentSort];

                    let modifier = (this.currentSortDir === 'desc') ? -1 : 1;

                    // Check if the values are numbers
                    if (!isNaN(aValue) && !isNaN(bValue)) {
                        // Compare as numbers
                        return (aValue - bValue) * modifier;
                    } else {
                        // Compare as strings
                        aValue = String(aValue);
                        bValue = String(bValue);
                        if (aValue < bValue) return -1 * modifier;
                        if (aValue > bValue) return 1 * modifier;
                        return 0;
                    }
                });
            },
            currentIndex() {
              return this.getIndex(this.showprices);
            },
            stats() {
                let stats = [
                                { 
                                    name: 'Pferde', 
                                    stat: this.horses.length, 
                                    previousStat: this.last_export.horse_count, 
                                    change: this.calculateChangedPercentage(this.horses.length, this.last_export.horse_count).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 }),
                                    changeType: this.horses.length > this.last_export.horse_count ? 'increase' : 'decrease'
                                },
                                { 
                                    name: 'Gesamtsumme', 
                                    stat: Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR'}).format(this.calculateGesamtsumme()), 
                                    previousStat: Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR'}).format(this.last_export.value), 
                                    change: this.calculateChangedPercentage(this.calculateGesamtsumme(), this.last_export.value).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 }), 
                                    changeType: this.calculateGesamtsumme() > this.last_export.value ? 'increase' : 'decrease'
                                }
                            ];
                stats.push();             
                return stats;
            }
        },
        mounted() {
            axios.get(route('farm.horses.lazy', this.farm))
                .then(x => {
                    this.horses = x.data;
                    this.final_loaded = true;
                })
        },
        methods: {
            confirm(text) {
                return confirm(text);
            },
            calculateChangedPercentage(a, b) {
                return (a / b) - 1;
            },
            calculateGesamtsumme() {
                return this.horses.reduce((total, horse) => total + horse.probable_invoice_sum, 0);
            },
            sort(s) {
                //if s == current sort, reverse
                if(s === this.currentSort) {
                    this.currentSortDir = this.currentSortDir == 'asc' ? 'desc' : 'asc';
                }
                this.currentSort = s;
            },
            updateOwner: function (x, horse) {
                if(x) {
                    horse.customer_id = x;
                    this.$inertia.post(route('horse.update.owner', horse), {'customer_id': x})
                }
            },
            updateStablePrice: function () {
                const stable = this.stables.find(x => x.id === this.stable_change_form.stable);
                this.stable_change_form.product = stable.current_price.pivot.product_id ?? null;
                this.stable_change_form.price = stable.current_price.pivot.value ?? null;
                this.stable_change_form.vat = stable.current_price.pivot.vat ?? 19;
            },
            updateStable: function (horse, x) {
                this.stable_change = true;
                this.stable_change_form.horse = horse;
                console.log(horse);
                if(x) {
                    this.stable_change = this.stables.find(y=> { y.id === x });
                    this.stable_date = true;
                    this.changing_horse = horse;
                    console.log(x, horse);
                }
            },
            goBack() {
              if (this.currentIndex > 0) {
                this.showprices = this.sortedHorses[this.currentIndex - 1];
              }
            },
            goForward() {
              if (this.currentIndex < this.sortedHorses.length - 1) {
                this.showprices = this.sortedHorses[this.currentIndex + 1];
              }
            },
            getIndex(horse) {
              return this.sortedHorses.findIndex(h => h.id === horse.id);
            },
            getPrice: function (horse) {
                if(!horse.probable_invoice) {
                    horse.probable_invoice = [
                        {
                            "date": new Date().toISOString().split('T')[0],
                            "exported": null,
                            "type": "message",
                            "value": 0,
                            "full_value": 0,
                            "quantity": 0,
                            "vat": 0,
                            "name": "Daten werden geladen",
                            "id": 0
                        }
                    ];
                }

                console.log(horse);
                this.showprices = horse;
                axios.get(route('horse.load_full', horse))
                    .then(x => {
                        horse = x.data;
                        this.horses.find(x => x.id === horse.id).probable_invoice = horse.probable_invoice;
                        this.horses.find(x => x.id === horse.id).probable_invoice_sum = horse.probable_invoice_sum;
                        if(this.showprices) this.showprices = horse;
                    })
            }
        }
    }
</script>
